/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { X } from 'lucide-react';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { theme } from 'styles/theme/theme';
import { Portal } from 'ud-ui/components/portal';
import { DivProps } from 'typings';

export default function useCustomModal(props?: IProps) {
  const { onClose, background } = props ?? {};
  const [state, setState] = useState<ModalState>({
    isOpen: false,
    modalStyle: {},
    iconProps: {},
    containerProps: {},
    overlayProps: {},
    childrenContainerProps: {},
  });

  const isOpen = useMemo(() => {
    return state.isOpen;
  }, [state.isOpen]);

  const close = useCallback(() => {
    setState({ isOpen: false, modalStyle: {} });
    onClose?.();
  }, [onClose]);

  const [closeIcon, setCloseIcon] = useState<ReactNode>();

  const open = useCallback((styles: ModalStyles, closeIcon?: React.ReactNode) => {
    setState({ isOpen: true, ...styles });
    setCloseIcon(closeIcon);
  }, []);

  const backgroundColor = useMemo(() => {
    switch (background) {
      case 'white':
        return theme.colors.SurfaceCard;
      case 'gray':
        return theme.colors.GrayF2;
      case 'black':
        return '#000000';
      default:
        return theme.colors.SurfaceCard;
    }
  }, [background]);

  const defaultOpen = useCallback(
    (additionalStyle?: Partial<ModalStyles>, closeIcon?: React.ReactNode) => {
      open(
        {
          ...additionalStyle,
          modalStyle: {
            padding: 0,
            zIndex: theme.zIndex.modal,
            position: 'fixed',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100vw',
            height: '100vh',
            backgroundColor,
            top: 0,
            left: 0,
            borderRadius: 0,
            borderColor: 'transparent',
            overflow: 'clip',
            background: theme.colors.Scrim,
            ...additionalStyle?.modalStyle,
          },
        },
        closeIcon,
      );
    },
    [backgroundColor, open],
  );

  useEffect(() => {
    const onPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    document.addEventListener('keydown', onPress);
    return () => {
      document.removeEventListener('keydown', onPress);
    };
  }, [close]);

  const Modal = useCallback(
    (props: DivProps) => {
      const { children, ...restProps } = props;
      return (
        <Portal wrapperId={Math.random().toString()}>
          {state.isOpen ? (
            <ModalContainer {...restProps} style={{ ...state.modalStyle, ...props.style }}>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  right: 0,
                  width: '100vw',
                  height: '100vh',
                }}
                onClick={close}
                {...state.overlayProps}
              />
              <div {...state.containerProps} css={css(modalStyle, state.containerProps?.css)}>
                <div className="icon-wrapper" css={iconModalCss} {...state.iconProps} onClick={close}>
                  {closeIcon ?? <X size={20} className="modal-close-icon cursor-pointer" color={theme.colors.InkMuted} />}
                </div>
                <div
                  {...state.childrenContainerProps}
                  style={{
                    ...state.childrenContainerProps?.style,
                    height: '100%',
                  }}
                >
                  {children}
                </div>
              </div>
            </ModalContainer>
          ) : (
            <></>
          )}
        </Portal>
      );
    },
    [
      close,
      closeIcon,
      state.childrenContainerProps,
      state.containerProps,
      state.iconProps,
      state.isOpen,
      state.modalStyle,
      state.overlayProps,
    ],
  );

  return { Modal, close, open, isOpen, defaultOpen };
}

interface IProps {
  onClose?: () => void;
  background?: 'white' | 'gray' | 'black';
}

interface ModalState extends ModalStyles {
  isOpen: boolean;
}

export interface ModalStyles {
  modalStyle: Partial<React.CSSProperties>;
  containerProps?: DivProps;
  overlayProps?: DivProps;
  iconProps?: DivProps;
  childrenContainerProps?: DivProps;
}

const modalStyle = css`
  /* Локальный стекинг внутри модалки: окно поверх соседнего div-перехватчика клика. */
  z-index: ${theme.zIndex.content};

  background-color: ${theme.colors.SurfaceCard};
  border: 1px solid ${theme.colors.LineSubtle};
  box-shadow: ${theme.elevation.high};
  width: 500px;
  min-height: 200px;
  border-radius: ${theme.radii.card};

  font-family:
    SF Pro Text,
    sans-serif;
  font-size: 20px;
  font-weight: 400;
  line-height: 20px;
  text-align: left;

  display: flex;
  flex-direction: column;

  padding: 34px 24px;
  position: relative;
`;

const iconModalCss = css`
  position: absolute !important;
  top: 0;
  right: 0;
  padding: 22px;
  cursor: pointer;
`;

const ModalContainer = styled.div`
  position: absolute;
  background-color: white;
  box-shadow: ${theme.elevation.medium};
  padding-block: 8px;
  z-index: ${theme.zIndex.modal};
  border-radius: ${({ theme }) => theme.radii.panel};
  display: flex;
  flex-direction: column;
`;
